/*
 * rcc-card-components.js — PAEX card custom components for the RCC PLP,
 * consolidated (review fix batch 2026-07-31, item S2) from the four former
 * parser-blocking files rcc-product-wishlist.js, rcc-product-inventory.js,
 * rcc-product-notify-me.js and rcc-units-last-30d.js into ONE blocking
 * script tag (they must still be registered before the RCC app block reads
 * its data requirements — see snippets/nimstrata.liquid). Behaviour is
 * unchanged except the audited fixes:
 *   S3 — inventory rows are built with createElement/textContent (the
 *        store name comes from DOM text / API location names and was
 *        previously interpolated into innerHTML);
 *   N3 — the pending rows no longer stamp data-available="undefined";
 *        the attribute is set only for real true/false answers;
 *   S1 — customer-facing strings read window.rccStrings (serialized from
 *        the paex.rcc.* locale keys by nimstrata.liquid), with the
 *        previous EN/FR literals kept as fallback.
 * rcc-product-badges.js is app-managed and stays its own file/tag.
 * M3 (2026-08-03): former modules 5-7 (size-pill "+N" chip, back-nav scroll
 * restore, back-button de-trap) moved VERBATIM to the deferred
 * rcc-plp-enhancements.js — they define no custom elements and run off
 * post-load events, so only the element registrations (1-4) still block.
 */

/* ------------------------------------------------------------------ */
/* shared i18n: locale strings serialized by nimstrata.liquid          */
/* ------------------------------------------------------------------ */

function rccCardStrings() {
  return (typeof window !== 'undefined' && window.rccStrings) || {};
}

/* ================================================================== */
/* 1 · rcc-product-wishlist (source: rcc-product-wishlist.js)          */
/* ================================================================== */

if (!customElements.get('rcc-product-wishlist')) {
  const wishlist = {
    swat: null,
    variants: new Set(),
    components: new Set(),
    /* App-off gate. The verdict is OWNED by
       snippets/paex-wishlist-app-state.liquid — inline in <head> ahead of
       nimstrata, so window.paexWishlistApp already exists here. Tri-state:
         null  Swym has not answered yet
         true  the Wishlist app is on
         false the Wishlist app is off
       A heart hides only on a definite false; that snippet explains why an
       unreadable answer must never count as one. */
    appEnabled: null,
  };

  const readAppState = () => (window.paexWishlistApp ? window.paexWishlistApp.enabled : null);
  wishlist.appEnabled = readAppState();

  const syncWishlist = () => wishlist.components.forEach((component) => component.updateState());
  const updateFromEvent = (event, added) => {
    const variantId = String(event.detail?.d?.epi || '');
    if (!variantId) return;
    wishlist.variants[added ? 'add' : 'delete'](variantId);
    syncWishlist();
  };
  /* Idempotent: the owner's SwymCallbacks entry and this file's have no
     guaranteed order, so whichever learns the real verdict first starts the
     sync, and the second one is a no-op. */
  let syncStarted = false;
  const startSync = () => {
    const swat = wishlist.swat;
    if (syncStarted || !swat || wishlist.appEnabled === false) return;
    syncStarted = true;

    swat.fetch((items) => {
      wishlist.variants = new Set((items || []).map((item) => String(item.epi)));
      syncWishlist();
    });
    swat.evtLayer?.addEventListener('sw:addedtowishlist', (event) => updateFromEvent(event, true));
    swat.evtLayer?.addEventListener('sw:removedfromwishlist', (event) => updateFromEvent(event, false));
  };

  const initializeWishlist = (swat) => {
    wishlist.swat = swat;
    wishlist.appEnabled = readAppState();
    syncWishlist();
    startSync();
  };

  document.addEventListener('paex:wishlist-app', (event) => {
    wishlist.appEnabled = event.detail ? event.detail.enabled : null;
    syncWishlist();
    startSync();
  });

  window.SwymCallbacks = window.SwymCallbacks || [];
  window.SwymCallbacks.push(initializeWishlist);

  /* Added-to-wishlist confirmation beat (merchant 2026-08-21). Driven from the
     click path ONLY — deliberately not from updateState(), which also runs for
     every card on the initial swat.fetch() sync and would set every
     already-wishlisted heart beating on page load. The class is cleared on
     animationend (the listener sits on the button; the animation runs on the
     svg inside it and bubbles). Keyframes: assets/rcc-custom.css §D-21. */
  const ADDED_BEAT_CLASS = 'rcc-product-wishlist__button--added';
  const playAddedBeat = (button) => {
    button.classList.remove(ADDED_BEAT_CLASS);
    void button.offsetWidth; // reflow, so a re-add inside the window restarts it
    button.classList.add(ADDED_BEAT_CLASS);
  };

  customElements.define(
    'rcc-product-wishlist',
    class RccProductWishlist extends HTMLElement {
      connectedCallback() {
        wishlist.components.add(this);
        this.render();
      }

      disconnectedCallback() {
        wishlist.components.delete(this);
      }

      set product(product) {
        this._product = product;
        this.render();
      }

      get ids() {
        return {
          product: String(this._product?.productId || '').split('/').pop(),
          variant: String(this._product?.variantId || '').split('/').pop(),
        };
      }

      render() {
        const { product: productId, variant: variantId } = this.ids;
        if (!productId || !variantId) return;

        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'rcc-product-wishlist__button';
        button.innerHTML = `
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path d="M19.67 5.47a4.75 4.75 0 0 0-6.78.48L12 6.84l-.89-.89a4.75 4.75 0 0 0-6.78-.48 4.91 4.91 0 0 0-.36 7.31l7.67 7.67a.5.5 0 0 0 .71 0l7.67-7.67a4.91 4.91 0 0 0-.35-7.31Z" />
          </svg>
        `;
        button.addEventListener('click', (event) => this.toggle(event));
        button.addEventListener('animationend', () => button.classList.remove(ADDED_BEAT_CLASS));
        this.replaceChildren(button);
        this.updateState();
      }

      updateState() {
        /* On the host, not the button: rcc-product-wishlist is the absolutely
           positioned 32×32 box, so [hidden] removes the whole overlay anchor
           without reflowing the tile. Only a definite false hides it. */
        this.toggleAttribute('hidden', wishlist.appEnabled === false);

        const button = this.querySelector('button');
        if (!button) return;
        const strings = rccCardStrings();
        const added = wishlist.variants.has(this.ids.variant);
        button.classList.toggle('swym-added', added);
        button.setAttribute(
          'aria-label',
          added ? strings.wishlistRemove || 'Remove from wishlist' : strings.wishlistAdd || 'Add to wishlist'
        );
        button.setAttribute('aria-pressed', String(added));
      }

      toggle(event) {
        event.preventDefault();
        event.stopPropagation();
        const button = event.currentTarget;
        const { product: productId, variant: variantId } = this.ids;
        if (!wishlist.swat || button.disabled || wishlist.appEnabled === false) return;

        const url = new URL(this._product.url, window.location.origin);
        url.search = '';
        const item = { empi: Number(productId), epi: Number(variantId), du: url.href };
        const added = wishlist.variants.has(variantId);
        button.disabled = true;

        /* Swym answers NEITHER callback when the Wishlist app is off on the
           account (rapps returns {app:"Wishlist", enabled:false}) — seen live
           2026-08-21. Without this the button stays disabled:true for the life
           of the card, so the heart is dead to every later click even once the
           app is back. The timeout only restores clickability; it deliberately
           does not touch the wishlist state, which never changed. */
        let stuck;
        const release = () => {
          clearTimeout(stuck);
          button.disabled = false;
        };
        stuck = setTimeout(release, 8000);

        wishlist.swat[added ? 'removeFromWishList' : 'addToWishList'](
          item,
          () => {
            wishlist.variants[added ? 'delete' : 'add'](variantId);
            release();
            syncWishlist();
            if (!added) playAddedBeat(button); // adds only — removal needs no confirmation
          },
          release
        );
      }
    }
  );
}

/* ================================================================== */
/* 2 · rcc-product-inventory (source: rcc-product-inventory.js)        */
/* ================================================================== */

function rccInventoryId(value) {
  return String(value || '').split('/').pop();
}

function rccInventoryCookie(name) {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : '';
}

const rccInventoryCache = new Map();
const rccInventoryQueues = new Map();

// Session-scoped persistence (fix round 2026-07-31): repeat navigation used
// to re-run the proxy lookup on every PLP load (~1-2s of "Checking…" per
// page). Confirmed true/false answers are kept in sessionStorage for 10
// minutes so a warm navigation paints final rows on first render. Errors
// (null) are never persisted, so transient failures retry next page.
// Review fix 2026-07-31 (perf b): the persisted map is capped at the newest
// RCC_INVENTORY_MAX_ENTRIES entries so a long browse session cannot grow
// the sessionStorage payload unbounded.
const RCC_INVENTORY_STORE_KEY = 'rcc-inv-cache-v1';
const RCC_INVENTORY_TTL_MS = 10 * 60 * 1000;
const RCC_INVENTORY_MAX_ENTRIES = 500;

function rccInventoryRestore() {
  try {
    const raw = sessionStorage.getItem(RCC_INVENTORY_STORE_KEY);
    if (!raw) return;
    const now = Date.now();
    const entries = JSON.parse(raw);
    Object.keys(entries).forEach((key) => {
      const entry = entries[key];
      if (entry && typeof entry.v === 'boolean' && now - entry.t < RCC_INVENTORY_TTL_MS) {
        rccInventoryCache.set(key, entry.v);
      }
    });
  } catch (error) {
    /* sessionStorage unavailable (private mode/quota) — cache stays in-memory */
  }
}

function rccInventoryPersist() {
  try {
    const now = Date.now();
    const raw = sessionStorage.getItem(RCC_INVENTORY_STORE_KEY);
    const entries = raw ? JSON.parse(raw) : {};
    Object.keys(entries).forEach((key) => {
      const entry = entries[key];
      if (!entry || typeof entry.v !== 'boolean' || now - entry.t >= RCC_INVENTORY_TTL_MS) delete entries[key];
    });
    rccInventoryCache.forEach((value, key) => {
      if (typeof value === 'boolean' && !entries[key]) entries[key] = { v: value, t: now };
    });
    let keys = Object.keys(entries);
    if (keys.length > RCC_INVENTORY_MAX_ENTRIES) {
      keys.sort((a, b) => entries[b].t - entries[a].t);
      keys.slice(RCC_INVENTORY_MAX_ENTRIES).forEach((key) => delete entries[key]);
    }
    sessionStorage.setItem(RCC_INVENTORY_STORE_KEY, JSON.stringify(entries));
  } catch (error) {
    /* best effort only */
  }
}

rccInventoryRestore();

// Quantities 1..N render the amber "Only {n} …" low-stock rows (design's
// Store Availability states; the prototype shows 2–3, threshold set to 5).
const RCC_INVENTORY_LOW_STOCK = 5;

// Tag helpers shared by the custom-bundle rule below (2026-08-12). The RCC
// payload exposes the product's tags in more than one place depending on
// which wave of data has landed; attributes.rcc_tags.text is the app's own
// copy, present on some payloads that carry no top-level `tags` (verified
// live). Comparison is case/whitespace-insensitive — merchant tags are typed
// by hand.
function rccHasTag(tags, wanted) {
  const want = String(wanted || '')
    .toLowerCase()
    .trim();
  if (!want || !Array.isArray(tags)) return false;
  return tags.some((tag) => String(tag).toLowerCase().trim() === want);
}

function rccProductTags(product) {
  const tags = product?.tags || product?.graphql?.tags || product?.attributes?.rcc_tags?.text;
  return Array.isArray(tags) ? tags : null;
}

// Custom bundles (2026-08-12): products whose Shopify product type is
// "custom bundle" are configured on their own page (the shopper picks the
// sizes/colours of the products inside the bundle), so the PLP card must
// carry NO add control — the direct-add circle would drop an unconfigured
// bundle straight into the cart, and the quick-add modal only knows about
// the bundle's own variants. rcc-product-inventory stamps [data-bundle] on
// every bundle card (it is the one component present on every card, and the
// dim/quick-add rules in rcc-custom.css already key off its attributes);
// the CSS then hides the add-to-basket form and the quick-add trigger.
//
// EITHER of two signals makes it a bundle:
//
//   type — the merchant-facing rule ("Product type = custom bundle"), read
//          from the batched Storefront enrichment (our own aliased
//          requirement first, then rcc-product-badges' alias from the same
//          query, then any plain field the payload happens to carry).
//          Authoritative, but it lands ~0.8s after the cards paint.
//   tag  — Bundle Builder stamps `bundle-builder-dummy` on the bundle's own
//          storefront product. It rides in the RCC SEARCH payload, i.e. it is
//          on the FIRST product assignment, so it answers at paint.
//
// Live catalog check 2026-08-12: product type "Custom Bundle" = 2 products,
// tag bundle-builder-dummy = the same 2, neither appears without the other.
// Deliberately NOT the "No Delivery" tag, which the merchant proposed: it
// matches 117 products (fireworks, helium tanks…) that are pickup-only but
// perfectly quick-addable.
const RCC_BUNDLE_PRODUCT_TYPE = 'custom bundle';
const RCC_BUNDLE_TAG = 'bundle-builder-dummy';

function rccProductIsBundle(product) {
  const type =
    product?.graphql?.paexProductType ||
    product?.graphql?.flairProductType ||
    product?.graphql?.productType ||
    product?.productType ||
    product?.type;
  if (String(type || '').toLowerCase().trim() === RCC_BUNDLE_PRODUCT_TYPE) return true;
  return rccHasTag(rccProductTags(product), RCC_BUNDLE_TAG);
}

function rccInventoryStoreName() {
  return document
    .querySelector('paex-store-chip[data-context="header"] [data-chip-name]')
    ?.textContent?.trim();
}

/* ------------------------------------------------------------------ */
/* BOPIS (2026-10-22, lane E, Track 3 step 8 / seams §1 + §3.7): the  */
/* card's answer for the CART'S service. render() stamps              */
/* data-fulfil="in|out|none" next to data-store-stock: none while no  */
/* cart mode exists (every Montréal session: nothing else changes),   */
/* in / out for the cart's service — ship = the DC entry of the       */
/* ?view=paex-stock map (> 0; an absent entry = unknown = none),       */
/* pickup / same-day = the store entry (> 0; absent = 0 = out), with  */
/* RCC's own storeAvailability node for that location as the fallback */
/* before the map lands. The map is fetched LAZILY (IntersectionObserver, */
/* one view fetch per product, through paexFulfil.stock → sessionStorage */
/* 'paex:stock', at most RCC_FULFIL_CONCURRENCY in flight) and never    */
/* blocks the card paint: the row + stamp land on the re-render. Badge  */
/* text = paex.fulfil.card_* through paexFulfil.t (never a literal);   */
/* rcc-custom.css DISABLES the add circle (grey, still pressable) and   */
/* hides the quick-add trigger on an out card in a PICKUP / SAME-DAY    */
/* cart (5.2); snippets/nimstrata.liquid swallows the circle's click    */
/* and says why (toast) — that swallow is the primary path (round 2     */
/* n=37). A SHIP cart (5.3, owner 2026-10-23) only flags the card       */
/* ("In store only", amber): the circle stays active and the press      */
/* goes through the bridge gate, where the interception explains.      */
/* ------------------------------------------------------------------ */
function rccFulfilApi() {
  return window.paexFulfil && typeof window.paexFulfil === 'object' ? window.paexFulfil : null;
}

function rccFulfilText(key, vars) {
  const api = rccFulfilApi();
  if (!api || typeof api.t !== 'function') return '';
  let out = '';
  try {
    out = String(api.t(key, vars || {}) || '');
  } catch (error) {
    out = '';
  }
  return /^Translation missing/i.test(out) ? '' : out;
}

// the cart's service + its location: { service, loc } or null when no mode.
// Round 2 (design matrix "Paused: no badge", n=38): a pickup / same-day cart whose
// store is paused TODAY (pickup_paused / local_delivery_paused / a blackout day,
// paexFulfil.pausedToday) answers null too — the cards fall back to the no-mode
// state (no green "Pickup at …", no grey "Not available", circle as today) while
// the drawer says the service is paused; the add path stays protected by
// paexFulfil.check / the Validation Function.
function rccFulfilMode() {
  const api = rccFulfilApi();
  if (!api || typeof api.mode !== 'function') return null;
  try {
    const mode = api.mode();
    if (!mode) return null;
    const service = api.service(mode);
    const loc = service === 'ship' ? api.onlineLocId() : api.locationId(mode);
    if (!service) return null;
    if (service !== 'ship' && typeof api.pausedToday === 'function' && typeof api.store === 'function') {
      const store = loc == null ? null : api.store(String(loc));
      if (store && api.pausedToday(store, service)) return null;
    }
    return { service, loc: loc == null ? '' : String(loc) };
  } catch (error) {
    return null;
  }
}

// Online truth for a card (Sasha 2026-10-24): since the Québec location fulfils online orders (the
// BOPIS pilot), Shopify's availableForSale / quantityAvailable count the store's stock too, so a
// Québec-only item read "In stock online" with a live add circle on the collection and search pages
// while its PDP (onlineAvailable() in paex-delivery-method.js) said "Sold out online". The DC entry
// of the ?view=paex-stock map is the PDP's source; it wins whenever it is known: dc > 0 = online,
// 0 = sold out online (the count is the DC count, capped by the view at the top tier). A null dc
// (metafield never synced) and an absent entry (map not landed, 404, variant unknown) keep
// Shopify's answer, so the card paints as today and corrects itself when the map lands (render()
// re-runs on fulfilFetch). A variant Shopify calls unavailable is sold out everywhere, whatever a
// stale DC entry says — the PDP's soldOutEverywhere rule.
function rccOnlineTruth(variant, entry) {
  const shopify = { available: variant.availableForSale, quantity: variant.quantityAvailable, known: false };
  if (!entry || entry.dc == null) return shopify;
  if (shopify.available === false) return { available: false, quantity: 0, known: true };
  const dc = Math.max(0, Number(entry.dc) || 0);
  return { available: dc > 0, quantity: dc, known: true };
}

const RCC_FULFIL_CONCURRENCY = 6;
const rccFulfilQueue = { active: 0, pending: [] };

function rccFulfilDrain() {
  while (rccFulfilQueue.active < RCC_FULFIL_CONCURRENCY && rccFulfilQueue.pending.length) {
    const job = rccFulfilQueue.pending.shift();
    rccFulfilQueue.active += 1;
    const api = rccFulfilApi();
    const done = (map) => {
      rccFulfilQueue.active -= 1;
      job.resolve(map && typeof map === 'object' ? map : {});
      rccFulfilDrain();
    };
    try {
      api.stock(job.handle).then(done, () => done({}));
    } catch (error) {
      done({});
    }
  }
}

// one ?view=paex-stock fetch per product (memoized + sessionStorage inside paexFulfil.stock)
function rccFulfilStock(handle) {
  const api = rccFulfilApi();
  if (!api || typeof api.stock !== 'function' || !handle) return Promise.resolve({});
  return new Promise((resolve) => {
    rccFulfilQueue.pending.push({ handle, resolve });
    rccFulfilDrain();
  });
}

// lazy: fetch when the card nears the viewport (one observer for every card)
let rccFulfilObserver = null;
function rccFulfilObserve(component) {
  if (typeof IntersectionObserver !== 'function') {
    component.fulfilFetch();
    return;
  }
  if (!rccFulfilObserver) {
    rccFulfilObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          rccFulfilObserver.unobserve(entry.target);
          if (typeof entry.target.fulfilFetch === 'function') entry.target.fulfilFetch();
        });
      },
      { rootMargin: '240px 0px' }
    );
  }
  rccFulfilObserver.observe(component);
}

// the mode moved (set / cleared / boot): every card re-answers
document.addEventListener('paex:fulfil-change', () => {
  document.querySelectorAll('rcc-product-inventory').forEach((component) => {
    if (typeof component.render === 'function') component.render();
  });
});

// S3: single DOM builder for every availability row. Text lands via
// textContent (store names come from DOM text / API location names — never
// markup); data-available is stamped only for real true/false answers (N3).
// Colour segmentation (merchant 2026-07-31 + design crop): the row is now
// TWO spans — the STATUS segment ("In stock", "Plus que 2", "Rupture de
// stock") inherits the row's state colour (dot included, unchanged), the
// REST segment ("en ligne", "à Ste-Thérèse") reads in standard ink via
// [data-rest] in rcc-custom.css. rest='' keeps a status-only row.
function rccInventoryRowElement(status, rest, available, state) {
  const item = document.createElement('li');
  item.className = 'rcc-product-inventory__row';
  if (available === true || available === false) item.dataset.available = String(available);
  if (state) item.dataset.state = state;
  const statusSpan = document.createElement('span');
  statusSpan.textContent = status;
  item.append(statusSpan);
  if (rest) {
    const restSpan = document.createElement('span');
    restSpan.dataset.rest = '';
    restSpan.textContent = rest;
    // a11y (W3.8 low, 2026-08-01): a whitespace text node between the two
    // spans — the row is display:flex, so flexbox drops whitespace-only
    // anonymous boxes (zero visual change), but text extraction and the
    // accessible name now read "In stock online", not "In stockonline".
    item.append(' ', restSpan);
  }
  return item;
}

function rccQueueInventoryLookup(component, placeId) {
  const productId = rccInventoryId(component._product?.productId);
  const cacheKey = `${placeId}:${productId}`;
  if (!productId || rccInventoryCache.has(cacheKey)) return;

  let queue = rccInventoryQueues.get(placeId);
  if (!queue) {
    queue = { components: new Set(), timer: null };
    rccInventoryQueues.set(placeId, queue);
  }
  queue.components.add(component);
  clearTimeout(queue.timer);
  queue.timer = setTimeout(() => rccLoadInventory(placeId), 0);
}

async function rccLoadInventory(placeId) {
  const queue = rccInventoryQueues.get(placeId);
  if (!queue) return;
  rccInventoryQueues.delete(placeId);

  const components = Array.from(queue.components).filter((component) => component.isConnected);
  const productIds = [...new Set(components.map((component) => rccInventoryId(component._product?.productId)))].filter(
    Boolean
  );
  const root = components[0]?.closest('.rcc-search');
  if (!root || !productIds.length) return;

  const placement = `projects/${root.dataset.projectName}/locations/global/catalogs/default_catalog/servingConfigs/${root.dataset.servingConfig}`;
  const payload = {
    filterFields: [],
    filters: [
      { field: 'id', value: productIds },
      { field: 'pickupInStore', value: [placeId] },
    ],
    languageCode: `${root.dataset.language}-${root.dataset.country}`,
    page: 1,
    pageSize: productIds.length,
    placement,
    placeIds: [placeId],
    query: '',
    visitorId: rccInventoryCookie('rcc-visitor-id'),
  };

  try {
    const response = await fetch('https://storefront.retailconnect.app/v1/search', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const data = await response.json();
    if (!response.ok || data.status !== 'fulfilled') throw new Error('Inventory lookup failed');

    const availableIds = new Set((data.value?.results || []).map((result) => String(result.id)));
    productIds.forEach((productId) => {
      rccInventoryCache.set(`${placeId}:${productId}`, availableIds.has(productId));
    });
    rccInventoryPersist();
  } catch (error) {
    productIds.forEach((productId) => rccInventoryCache.set(`${placeId}:${productId}`, null));
  }

  components.forEach((component) => component.render());
}

if (!customElements.get('rcc-product-inventory')) {
  customElements.define(
    'rcc-product-inventory',
    class RccProductInventory extends HTMLElement {
      // PERF (fix round 3, 2026-07-31): this component no longer declares its
      // own graphqlRequirements. The old duplicate
      // `rccInventoryVariants: variants(first:100){ ... storeAvailability(first:100) }`
      // block added ~0.4s to the app's ProductCardBatch Storefront call (the
      // request that gates pills/inventory/rating hydration on every PLP
      // view, measured by replaying the captured query with and without the
      // block), while returning ZERO storeAvailability nodes in production —
      // local-pickup availability is not populated on this shop, so the
      // store row has always been served by the batched RCC proxy lookup
      // below. Online-row data (availableForSale, quantityAvailable) is read
      // from the app's OWN variants fragment, which the card builder always
      // fetches for its option pills and prices. rccInventoryVariants is
      // still read first for backward compatibility if a cached build
      // provides it.
      //
      // The ONE field this component asks for (2026-08-12) is the scalar
      // productType, needed for the custom-bundle rule above: a single
      // scalar on the product node adds no round trip and no connection to
      // the batched ProductCardBatch call (the block removed for perf was a
      // duplicate variants(first:100) + storeAvailability(first:100)
      // traversal). Aliased so it can never collide with the app's own
      // selection or rcc-product-badges' flairProductType. (Live-probed
      // 2026-08-12: the app merges every component's requirements into ONE
      // per-card graphql object shared by all components, so flairProductType
      // is already readable here — this alias only makes the field ours, so
      // the rule survives rcc-product-badges being reconfigured or removed.)
      static get graphqlRequirements() {
        return { graphql: ['paexProductType: productType'] };
      }

      connectedCallback() {
        this.abortController = new AbortController();
        window.addEventListener('retail-connect:place:set', () => this.render(), {
          signal: this.abortController.signal,
        });
        window.addEventListener('retail-connect:place:removed', () => this.render(), {
          signal: this.abortController.signal,
        });
        this.render();
      }

      disconnectedCallback() {
        this.abortController?.abort();
        if (rccFulfilObserver) rccFulfilObserver.unobserve(this);
        if (this._fulfilCircleObserver) {
          this._fulfilCircleObserver.disconnect();
          this._fulfilCircleObserver = null;
        }
      }

      set product(product) {
        this._product = product;
        this.render();
      }

      // BOPIS: the product handle for the stock view (payload → graphql → the card's own anchor)
      fulfilHandle() {
        const product = this._product || {};
        let handle = product.handle || product.graphql?.handle || '';
        if (!handle && product.url) {
          const match = String(product.url).match(/\/products\/([^/?#]+)/);
          if (match) handle = match[1];
        }
        if (!handle) {
          const anchor = this.closest('.rcc-search__product')?.querySelector('a[href*="/products/"]');
          const match = anchor && anchor.pathname.match(/\/products\/([^/?#]+)/);
          if (match) handle = match[1];
        }
        return handle ? decodeURIComponent(handle) : '';
      }

      // BOPIS: the stock map for this product (once per handle; render() re-reads it when it lands)
      fulfilFetch() {
        const handle = this.fulfilHandle();
        if (!handle || this._fulfilStockFor === handle) return;
        this._fulfilStockFor = handle;
        rccFulfilStock(handle).then((map) => {
          if (!this.isConnected || this._fulfilStockFor !== handle) return;
          this._fulfilStock = map;
          this.render();
        });
      }

      // BOPIS round 2 (design 5.2, n=37): on an out card the direct-add circle is DISABLED, not
      // removed — rcc-custom.css greys it (cursor not-allowed) and keeps it clickable so the
      // capture listener in snippets/nimstrata.liquid swallows the press and pops the
      // card_tooltip / card_tooltip_local toast (the touch equivalent of the design's tooltip);
      // aria-disabled + title + aria-describedby carry the same string for hover and screen
      // readers. A card that is not out gets every stamp removed again (mode cleared, stock
      // landed, store switch). The quick-add trigger is untouched: it is stamped per selected
      // size and the quick view stays reachable so the shopper can pick another service.
      // Design 5.3 (owner decision 2026-10-23): a SHIP cart never disables the circle — a DC-out
      // item stays addable, the press reaches the bridge gate (nimstrata.liquid) and the
      // interception explains; only the pickup / same-day out case keeps the disabled circle.
      fulfilCircle(out, service) {
        out = out === true && service !== 'ship';
        this._fulfilCircleState = { out, service: service || '' };
        const card = this.closest('.rcc-search__product');
        const circle = card?.querySelector(
          '.rcc-search__product__add-to-basket-form > .rcc-search__product__add-to-basket'
        );
        if (!circle || circle.classList.contains('BIS_trigger')) return;
        const text = out ? (service === 'local' && rccFulfilText('card_tooltip_local')) || rccFulfilText('card_tooltip') : '';
        if (out && text) this.fulfilCircleWatch(card);
        if (out && text) {
          circle.setAttribute('aria-disabled', 'true');
          circle.setAttribute('title', text);
          let hint = circle.querySelector('[data-fulfil-hint]');
          if (!hint) {
            hint = document.createElement('span');
            hint.className = 'visually-hidden';
            hint.setAttribute('data-fulfil-hint', '');
            hint.id = 'paex-fulfil-hint-' + Math.random().toString(36).slice(2, 9);
            circle.append(hint);
          }
          if (hint.textContent !== text) hint.textContent = text;
          circle.setAttribute('aria-describedby', hint.id);
        } else if (circle.hasAttribute('aria-disabled') || circle.querySelector('[data-fulfil-hint]')) {
          circle.removeAttribute('aria-disabled');
          circle.removeAttribute('title');
          circle.removeAttribute('aria-describedby');
          const hint = circle.querySelector('[data-fulfil-hint]');
          if (hint) hint.remove();
        }
      }

      // The stamps live on the APP'S button: when Retail Connect rebuilds its form (its own
      // add / busy cycle) they would vanish until the next render. One observer per card
      // (childList, subtree) re-runs fulfilCircle with the last verdict as soon as an out
      // card's circle is found without its aria-disabled stamp; a card that is not out, or
      // one already stamped, is a no-op, so the hint span fulfilCircle appends never loops.
      // The visual (rcc-custom.css) and the add guard (snippets/nimstrata.liquid) do not
      // depend on the stamps — this only keeps the AT hints in step with them.
      fulfilCircleWatch(card) {
        if (this._fulfilCircleObserver || !card || typeof MutationObserver !== 'function') return;
        this._fulfilCircleObserver = new MutationObserver(() => {
          const state = this._fulfilCircleState;
          if (!state || !state.out || !this.isConnected) return;
          const circle = card.querySelector(
            '.rcc-search__product__add-to-basket-form > .rcc-search__product__add-to-basket'
          );
          if (circle && !circle.hasAttribute('aria-disabled')) this.fulfilCircle(true, state.service);
        });
        this._fulfilCircleObserver.observe(card, { childList: true, subtree: true });
      }

      // Does the RESOLVED store (paexFulfil.storeId — the chip's / the session's, never the DC a ship
      // mode carries) have this variant? Stock-map entry first, RCC's own storeAvailability node for
      // that location next, the batched lookup's cached answer when RCC's place IS that store. null =
      // unknown.
      resolvedStoreHas(entry, storeAvailabilityNodes, placeId, cachedAvailability) {
        const api = rccFulfilApi();
        let storeLoc = '';
        try { storeLoc = api && typeof api.storeId === 'function' ? rccInventoryId(api.storeId()) : ''; } catch (error) { storeLoc = ''; }
        if (!storeLoc) return null;
        if (entry && entry.stores) return Number(entry.stores[storeLoc]) > 0;
        const node = (storeAvailabilityNodes || []).find((availability) => rccInventoryId(availability.location?.id) === storeLoc);
        if (node) return node.available === true;
        if (placeId === storeLoc && typeof cachedAvailability === 'boolean') return cachedAvailability;
        return null;
      }

      // The card's notify pill (rcc-product-notify-me, a sibling slot) follows the same online truth:
      // it reads Shopify's availableForSale on its own, so a Québec-only item would never offer
      // "Notify me" while its circle is hidden. Hand it the verdict; it re-renders.
      syncNotify(online) {
        const card = this.closest('.rcc-search__product');
        const notify = card && card.querySelector('rcc-product-notify-me');
        if (!notify) return;
        const prev = notify._online;
        if (prev && prev.available === online.available && prev.known === online.known && prev.pickupPath === online.pickupPath) return;
        notify._online = online;
        if (typeof notify.render === 'function') notify.render();
      }

      // BOPIS: stamp data-fulfil + the card_* row for the cart's service (no mode → 'none', nothing else)
      renderFulfil(variant, placeId, storeAvailabilityNodes, cachedAvailability, list) {
        const mode = rccFulfilMode();
        if (!mode) {
          this.setAttribute('data-fulfil', 'none');
          this.fulfilCircle(false, '');
          return;
        }
        const variantId = rccInventoryId(variant.id);
        const entry = this._fulfilStock && this._fulfilStock[variantId];
        let verdict = null;
        if (entry) {
          if (mode.service === 'ship') verdict = entry.dc == null ? null : Number(entry.dc) > 0;
          else verdict = Number(entry.stores && entry.stores[mode.loc]) > 0;
        } else if (mode.service !== 'ship') {
          const node = (storeAvailabilityNodes || []).find(
            (availability) => rccInventoryId(availability.location?.id) === mode.loc
          );
          if (node) verdict = node.available === true;
          else if (placeId === mode.loc && typeof cachedAvailability === 'boolean') verdict = cachedAvailability;
        }
        if (!entry && this.isConnected) rccFulfilObserve(this); // lazy: the map answers on the re-render
        this.setAttribute('data-fulfil', verdict === true ? 'in' : verdict === false ? 'out' : 'none');
        this.fulfilCircle(verdict === false && mode.service !== 'ship', mode.service); // 5.3: a ship cart never disables the circle
        if (verdict === null || !list) return;
        const api = rccFulfilApi();
        let storeName = '';
        try {
          storeName = (mode.loc && typeof api.storeName === 'function' && api.storeName(mode.loc)) || rccInventoryStoreName() || '';
        } catch (error) {
          storeName = rccInventoryStoreName() || '';
        }
        // Visual review 2026-10-23 (#34, design 5.1): the card names the store in its SHORT form
        // ("Pickup at Québec", never "Pickup at Party Expert - Québec") — the same helper the chip
        // and the sticky bar use; the full name stays in the drawer header / checkout button.
        if (storeName && typeof paexDeliveryShortName === 'function') {
          try { storeName = paexDeliveryShortName(storeName) || storeName; } catch (error) { /* full name */ }
        }
        let text = '';
        if (mode.service === 'pickup') text = verdict ? rccFulfilText('card_pickup_in', { store: storeName }) : rccFulfilText('card_pickup_out');
        else if (mode.service === 'local') text = verdict ? rccFulfilText('card_local_in', { store: storeName }) : rccFulfilText('card_local_out'); // round 2 n=41: "same as pickup"
        else {
          // Sasha 2026-10-23: "In store only" ONLY when the resolved store actually has it — an item out online
          // AND not carried at the store gets no mode line (the native rows already say both)
          let storeHas = null;
          // the RESOLVED store (the chip's / the session's, paexFulfil.storeId) — never the place id RCC uses
          // for its own lookup and never the DC that a ship mode carries as its location
          let storeLoc = '';
          try { storeLoc = typeof api.storeId === 'function' ? rccInventoryId(api.storeId()) : ''; } catch (error) { storeLoc = ''; }
          if (entry && storeLoc) storeHas = Number(entry.stores && entry.stores[storeLoc]) > 0;
          else {
            const storeNode = storeLoc ? (storeAvailabilityNodes || []).find((availability) => rccInventoryId(availability.location?.id) === storeLoc) : null;
            if (storeNode) storeHas = storeNode.available === true;
            else if (typeof cachedAvailability === 'boolean') storeHas = cachedAvailability;
          }
          text = verdict || storeHas !== true ? '' : rccFulfilText('card_ship_out');
        }
        if (!text) return;
        // 5.3 "In store only" reads amber (the card's low-stock state); 5.2 "Not available for
        // pickup" stays grey (none)
        const row = rccInventoryRowElement(text, '', verdict, verdict ? undefined : mode.service === 'ship' ? 'low' : 'none');
        row.setAttribute('data-fulfil-row', mode.service);
        // ONE mode line (#34, states 5.1–5.3): in a pickup / same-day cart the service line REPLACES
        // the store tag ("In stock at Québec" + "Pickup at Québec" said the same thing twice); the
        // "In stock online" row stays as in no-mode. A ship cart keeps both rows and adds 5.3.
        if (mode.service !== 'ship') {
          const storeRow = list.querySelector('[data-store-row]');
          if (storeRow) storeRow.remove();
        }
        list.append(row);
      }

      labels() {
        const strings = rccCardStrings();
        const fallback = document.documentElement.lang.toLowerCase().startsWith('fr')
          ? {
              online: 'en ligne',
              store: 'en magasin',
              atStore: 'à',
              inStock: 'En stock',
              outOfStock: 'Rupture de stock',
              checking: 'Vérification…',
              unavailable: 'Disponibilité non disponible',
              onlyLeftOnline: 'Plus que {count} en ligne',
              onlyAtStore: 'Plus que {count} à {store}',
              notCarriedAtStore: 'Non offert à {store}',
              notCarriedStore: 'Non offert en magasin',
              onlyLeft: 'Plus que {count}',
              onlyCount: 'Plus que {count}',
              notCarried: 'Non offert',
            }
          : {
              online: 'online',
              store: 'in store',
              atStore: 'at',
              inStock: 'In stock',
              outOfStock: 'Out of stock',
              checking: 'Checking…',
              unavailable: 'Availability unavailable',
              onlyLeftOnline: 'Only {count} left online',
              onlyAtStore: 'Only {count} at {store}',
              notCarriedAtStore: 'Not carried at {store}',
              notCarriedStore: 'Not carried in store',
              onlyLeft: 'Only {count} left',
              onlyCount: 'Only {count}',
              notCarried: 'Not carried',
            };
        Object.keys(fallback).forEach((key) => {
          if (typeof strings[key] === 'string' && strings[key]) fallback[key] = strings[key];
        });
        return fallback;
      }

      renderChecking(labels) {
        // Anti-pop-in (fix rounds 2026-07-31): render the real "Checking…"
        // rows so the availability band has its settled height from the
        // first card paint — the text then swaps in place with zero layout
        // shift. Used both before the app assigns `product` AND while a
        // product payload exists without variant data yet (without our own
        // graphql requirement the app assigns product before its
        // ProductCardBatch enrichment resolves). data-rcc-loading is the
        // CSS hook for that window.
        const placeholderPlaceId = rccInventoryId(window.Nimstrata?.placeId || rccInventoryCookie('rcc-place-id'));
        const placeholderStore = rccInventoryStoreName();
        this.setAttribute('data-rcc-loading', '');
        const list = document.createElement('ul');
        list.append(rccInventoryRowElement(labels.checking, labels.online));
        if (placeholderPlaceId) {
          list.append(
            rccInventoryRowElement(
              labels.checking,
              placeholderStore ? `${labels.atStore} ${placeholderStore}` : labels.store
            )
          );
        }
        this.replaceChildren(list);
      }

      render() {
        const labels = this.labels();

        if (!this._product) {
          this.renderChecking(labels);
          return;
        }

        // Custom-bundle marker (2026-08-12) — stamped before the variant
        // guard below so a card whose variant data has not landed yet still
        // loses its add control on the first render that has a product.
        this.toggleAttribute('data-bundle', rccProductIsBundle(this._product));

        const variants =
          this._product.graphql?.rccInventoryVariants?.nodes || this._product.graphql?.variants?.nodes || [];
        const variantId = rccInventoryId(this._product.variantId);
        const variant = variants.find((item) => rccInventoryId(item.id) === variantId) || variants[0];
        if (!variant) {
          // Product payload present but variant data not enriched yet —
          // keep the full-height "Checking…" band (every product has at
          // least one variant, so this is always the pending state).
          this.renderChecking(labels);
          return;
        }
        this.removeAttribute('data-rcc-loading');

        const placeId = rccInventoryId(window.Nimstrata?.placeId || rccInventoryCookie('rcc-place-id'));
        const storeAvailability = (variant.storeAvailability?.nodes || []).find(
          (availability) => rccInventoryId(availability.location?.id) === placeId
        );
        const cacheKey = `${placeId}:${rccInventoryId(this._product.productId)}`;
        const cachedAvailability = rccInventoryCache.get(cacheKey);
        const storeAvailable = storeAvailability ? storeAvailability.available : cachedAvailability;
        const storeName = storeAvailability?.location?.name || rccInventoryStoreName();
        if (placeId && !storeAvailability && !rccInventoryCache.has(cacheKey) && this.isConnected) {
          rccQueueInventoryLookup(this, placeId);
        }

        // Machine-readable store answer for the dim rule (merchant
        // 2026-07-31: OOS online + IN STOCK at the selected store must NOT
        // look sold out). rcc-custom.css keys the D-04 dim on
        // :not(:has([data-store-stock='true'])). Values: true/false real
        // answers; none = no store selected OR lookup error/pending (we
        // know nothing better → the dim applies for online-OOS). Re-stamped
        // on every render, which place:set/place:removed and the batched
        // lookup already re-trigger — store switches flip the dim live.
        this.setAttribute(
          'data-store-stock',
          placeId ? (storeAvailable === true ? 'true' : storeAvailable === false ? 'false' : 'none') : 'none'
        );

        // BOPIS (Sasha 2026-10-24): the online answer is the DC entry of the stock map when it is known
        // (rccOnlineTruth); the map is fetched lazily for every card Shopify calls available — the only
        // cards whose answer can change — mode or not. rccFulfilObserve is idempotent per element and
        // fulfilFetch is once per handle (memoized + sessionStorage inside paexFulfil.stock).
        const fulfilEntry = this._fulfilStock && this._fulfilStock[rccInventoryId(variant.id)];
        const online = rccOnlineTruth(variant, fulfilEntry);
        if (!fulfilEntry && variant.availableForSale === true && rccFulfilApi() && this.isConnected) rccFulfilObserve(this);
        const onlineAvailable = online.available;
        // Markup contract kept: ul > li.rcc-product-inventory__row with
        // data-available only on true/false rows (N3); data-state
        // "low"/"none" is ADDITIVE (styled in rcc-custom.css).
        const row = (available, context) => {
          const status =
            available === true
              ? labels.inStock
              : available === false
                ? labels.outOfStock
                : available === null
                  ? labels.unavailable
                  : labels.checking;
          return rccInventoryRowElement(status, context, available);
        };
        const isLowQuantity = (quantity) =>
          typeof quantity === 'number' && quantity >= 1 && quantity <= RCC_INVENTORY_LOW_STOCK;

        // Online row: amber "Only {n} left online" when 1..threshold remain.
        const onlineRow =
          onlineAvailable === true && isLowQuantity(online.quantity)
            ? rccInventoryRowElement(
                labels.onlyLeft.replace('{count}', online.quantity),
                labels.online,
                true,
                'low'
              )
            : row(onlineAvailable, labels.online);

        // Store row: green "In stock at {store}" / amber "Only {n} at {store}"
        // / gray "Not carried at {store}" (replaces the old "Out of stock at"
        // wording — covers both zero stock and not-stocked). The cached
        // proxy-lookup path has no quantity, so low needs a storeAvailability
        // node; checking/unavailable states unchanged.
        let storeRow = null;
        if (placeId) {
          const storeQuantity = storeAvailability?.quantityAvailable;
          if (storeAvailable === false) {
            storeRow = rccInventoryRowElement(
              labels.notCarried,
              storeName ? `${labels.atStore} ${storeName}` : labels.store,
              false,
              'none'
            );
          } else if (storeAvailable === true && storeName && isLowQuantity(storeQuantity)) {
            storeRow = rccInventoryRowElement(
              labels.onlyCount.replace('{count}', storeQuantity),
              `${labels.atStore} ${storeName}`,
              true,
              'low'
            );
          } else {
            storeRow = row(storeAvailable, storeName ? `${labels.atStore} ${storeName}` : labels.store);
          }
        }

        const list = document.createElement('ul');
        list.append(onlineRow);
        if (storeRow) {
          storeRow.setAttribute('data-store-row', ''); // the row a pickup / same-day mode line replaces (renderFulfil)
          list.append(storeRow);
        }
        // data-online (Sasha 2026-10-24): in / out from rccOnlineTruth ('none' = Shopify has no answer
        // yet). On a KNOWN out card (the DC entry says 0 while Shopify still sells it) rcc-custom.css
        // hides the direct-add circle, exactly as the app hides it for a sold-out variant, and the
        // notify pill takes its place — UNLESS the card has a pickup path: the store experience
        // (paexFulfil.gated) or a pickup / same-day cart AND the resolved store has it; then the
        // circle stays (the press reaches the bridge gate, which resolves the service by stock) and
        // the pill stays away, stamped data-online-add='pickup'. snippets/nimstrata.liquid swallows
        // the press on a hidden-by-CSS circle as the no-:has() backstop.
        this.setAttribute('data-online', onlineAvailable === true ? 'in' : onlineAvailable === false ? 'out' : 'none');
        let pickupPath = false;
        if (online.known && onlineAvailable === false) {
          try {
            const api = rccFulfilApi();
            const mode = rccFulfilMode();
            const gated = !!(api && typeof api.gated === 'function' && api.gated());
            if (gated || (mode && mode.service !== 'ship')) {
              pickupPath = this.resolvedStoreHas(fulfilEntry, variant.storeAvailability?.nodes || [], placeId, cachedAvailability) === true;
            }
          } catch (error) {
            pickupPath = false;
          }
        }
        if (pickupPath) this.setAttribute('data-online-add', 'pickup');
        else this.removeAttribute('data-online-add');
        this.syncNotify({ available: onlineAvailable, known: online.known, pickupPath });
        // BOPIS: the cart's-service answer (stamp + row) — additive, 'none' without a mode
        try {
          this.renderFulfil(variant, placeId, variant.storeAvailability?.nodes || [], cachedAvailability, list);
        } catch (error) {
          this.setAttribute('data-fulfil', 'none');
        }
        this.replaceChildren(list);
      }
    }
  );
}

/* ================================================================== */
/* 3 · rcc-product-notify-me (source: rcc-product-notify-me.js)        */
/* ================================================================== */

if (!customElements.get('rcc-product-notify-me')) {
  customElements.define(
    'rcc-product-notify-me',
    class RccProductNotifyMe extends HTMLElement {
      set product(product) {
        this._product = product;
        this.render();
      }

      render() {
        if (!this._product) return;
        const variants = this._product.graphql?.variants?.nodes || [];
        const variantId = String(this._product.variantId || '').split('/').pop();
        const variant = variants.find((item) => String(item.id || '').endsWith(`/${variantId}`)) || variants[0];
        // Sasha 2026-10-24: the online truth comes from rcc-product-inventory (syncNotify: the DC entry
        // of the stock map, see rccOnlineTruth) once it is known; Shopify's availableForSale until then.
        // A card with a pickup path (data-online-add) keeps its circle instead of the pill.
        const online = this._online && this._online.known ? this._online.available : variant && variant.availableForSale;
        if (!variant || online || (this._online && this._online.pickupPath)) {
          this.replaceChildren();
          return;
        }

        const strings = rccCardStrings();
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'BIS_trigger rcc-search__product__add-to-basket';
        button.dataset.productHandle = this._product.handle || this._product.graphql?.handle || '';
        button.dataset.variantId = variantId;
        button.textContent =
          strings.notifyMe ||
          (document.documentElement.lang.toLowerCase().startsWith('fr')
            ? 'Prévenez-moi quand disponible'
            : 'Notify me when available');
        this.replaceChildren(button);
      }
    }
  );
}

/* ================================================================== */
/* 4 · rcc-units-last-30d (source: rcc-units-last-30d.js)              */
/* ================================================================== */
/*
 * Renders the design's social-proof line: "100+ bought in past month".
 *
 * Contract (nimstrata.com/docs/shopify/custom-components/): the app assigns
 * `element.product = { ...product, metafields, graphql }` via a property
 * setter — no attributes, no events. Metafields configured in the builder
 * (namespace `custom`, key `units_last_30d`) arrive as an ordered array at
 * product.metafields; entries may be absent when Shopify has no value, and
 * their exact shape is not documented — extraction below guards both
 * {namespace, key, value} objects and bare scalar entries.
 *
 * Output: textContent only (XSS-safe). Renders EMPTY when the metafield is
 * missing/unparseable/below 10 — rcc-custom.css collapses :empty.
 */

const RCC_UNITS_LAST_30D_BUCKETS = [
  [1000, '1K+'],
  [500, '500+'],
  [100, '100+'],
  [50, '50+'],
  [30, '30+'],
  [10, '10+'],
];

if (!customElements.get('rcc-units-last-30d')) {
  customElements.define(
    'rcc-units-last-30d',
    class RccUnitsLast30d extends HTMLElement {
      connectedCallback() {
        this.render();
      }

      set product(product) {
        this._product = product;
        this.render();
      }

      labels() {
        const strings = rccCardStrings();
        if (strings.unitsSuffix) return { suffix: strings.unitsSuffix };
        return document.documentElement.lang.toLowerCase().startsWith('fr')
          ? { suffix: 'achetés au cours du dernier mois' }
          : { suffix: 'bought in past month' };
      }

      metafieldValue() {
        const metafields = this._product?.metafields;
        if (!Array.isArray(metafields)) return '';
        for (const field of metafields) {
          if (field === null || field === undefined) continue;
          if (typeof field !== 'object') return String(field);
          const namespace = field.namespace ?? field.metafieldNamespace;
          const key = field.key ?? field.metafieldKey;
          if (namespace != null && key != null && `${namespace}.${key}` !== 'custom.units_last_30d') continue;
          if (field.value !== null && field.value !== undefined) return this.unitsOf(field.value);
        }
        return '';
      }

      // The metafield is JSON {"units": N, "as_of": "YYYY-MM-DD"} (store,
      // 2026-10-09); it may arrive parsed or as a JSON string. A plain
      // number / "100+" string is returned as is.
      unitsOf(value) {
        if (value && typeof value === 'object') return value.units == null ? '' : String(value.units);
        const text = String(value).trim();
        if (text.charAt(0) === '{') {
          try {
            const parsed = JSON.parse(text);
            return parsed && parsed.units != null ? String(parsed.units) : '';
          } catch (error) {
            return '';
          }
        }
        return text;
      }

      formatCount(raw) {
        const value = String(raw).trim();
        if (!value) return '';
        // Already formatted ("100+", "1K+", "1.2k+") → display verbatim.
        if (/^\d+(?:[.,]\d+)?\s*[kK]?\+$/.test(value)) return value;
        const count = Number(value.replace(/[^\d.-]/g, ''));
        if (!Number.isFinite(count) || count < 10) return '';
        const bucket = RCC_UNITS_LAST_30D_BUCKETS.find(([minimum]) => count >= minimum);
        if (!bucket) return '';
        return bucket[0] === 1000 ? `${Math.floor(count / 1000)}K+` : bucket[1];
      }

      render() {
        const formatted = this.formatCount(this.metafieldValue());
        this.textContent = formatted ? `${formatted} ${this.labels().suffix}` : '';
      }
    }
  );
}
